import { APP_SUPPORTED_URL_SCHEMES } from "@/const/app";
import { deviceModule } from "@/store/device";
import { PRODUCT_SLUG_CAITECH_CARE, VALID_PRODUCT_SLUGS } from "@/types/Master";

export function openInNewWindow(url: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).cordova !== undefined) {
    // システムのWebブラウザーで開く
    // @ts-ignore
    window.cordova.InAppBrowser.open(url, "_system");
  } else {
    window.open(url, "_blank");
  }
}

/**
 * モバイル端末のブラウザで表示されているか否かを返す
 */
export function isMobileBrowser() {
  return (
    deviceModule.isBrowser &&
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  );
}

/**
 * iOSのブラウザで表示されているか否かを返す
 */
export function isIosBrowser() {
  return (
    deviceModule.isBrowser && /iPhone|iPad|iPod/i.test(navigator.userAgent)
  );
}

/**
 * Androidのブラウザで表示されているか否かを返す
 */
export function isAndroidBrowser() {
  return deviceModule.isBrowser && /Android/i.test(navigator.userAgent);
}

/**
 * Cordovaでフォーカスinput要素にフォーカスしキーボードが表示されたときに、
 * フォーカスした要素が画面外になってしまうことがある問題を修正するための処理
 * https://github.com/cjpearson/cordova-plugin-keyboard/issues/62#issuecomment-337952646
 */
export function fixCordovaKeyboard() {
  // キーボードが表示された時に、フォーカスされている要素が見えるようにスクロールする
  if (!window.cordova) return;
  // キーボードが現在表示されているかどうかを保持する
  let isKeyboardShown = false;
  window.addEventListener("keyboardDidShow", function() {
    if (isKeyboardShown) return;
    const activeElement = document.activeElement;
    if (!activeElement) return;
    // activeElementが画面内にあるかどうかを判定する
    const rect = activeElement.getBoundingClientRect();
    if (rect.top >= 0 && rect.bottom <= window.innerHeight) return;
    // activeElementを画面内中央に表示する
    const absoluteElementTop = rect.top + window.scrollY;
    const y = absoluteElementTop + rect.height / 2 - window.innerHeight / 2;
    window.scrollTo({ top: y, behavior: "smooth" });
    isKeyboardShown = true;
  });
  window.addEventListener("keyboardDidHide", function() {
    isKeyboardShown = false;
  });
}

/**
 * ユーザーエージェントから productSlug を抽出する
 * 例: "Mozilla/5.0 (iPhone; CPU iPhone OS 18_3_2 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 CaisukeAPP/caitech_pharmacist hoge"
 * から "caitech_pharmacist" を抽出
 */
export function getProductSlugFromUserAgent(): string {
  const userAgent = navigator.userAgent;

  // "CaisukeAPP/" の後に続く文字列を抽出する
  const regex = /CaisukeAPP\/([^/\s]+)/;
  const match = userAgent.match(regex);

  if (match && match[1]) {
    const productSlug = match[1];
    return VALID_PRODUCT_SLUGS.includes(productSlug)
      ? productSlug
      : PRODUCT_SLUG_CAITECH_CARE;
  } else {
    return PRODUCT_SLUG_CAITECH_CARE;
  }
}

/**
 * URLスキーマからアプリ内パスを抽出する
 * @param url スキーマ付きURL (例: "caitech-pharmacist-staging1://orders/123")
 * @returns 抽出されたパス (例: "/orders/123") またはスキーマが一致しない場合は空文字列
 */
export function extractPathFromSchemeUrl(url: string): string {
  const supportedSchemes = APP_SUPPORTED_URL_SCHEMES!.split(",");

  for (const scheme of supportedSchemes) {
    const schemePrefix = `${scheme}://`;
    if (url.startsWith(schemePrefix)) {
      return url.replace(schemePrefix, "/");
    }
  }

  return "";
}
